import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import PersonIcon from '@material-ui/icons/Person';
import StorageIcon from '@material-ui/icons/Storage';
import { InfoCard } from '@backstage/core-components';
import type { DevxpApi } from '../api';
import type { DevxpConfig, UnmaskResult, GithubSyncConfig } from '../types';

const useStyles = makeStyles(theme => ({
  configGrid: {
    marginBottom: theme.spacing(3),
  },
  statusChip: {
    marginLeft: theme.spacing(1),
  },
  unmaskResult: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  resultLabel: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  resultValue: {
    fontFamily: 'monospace',
    fontSize: '1.1em',
  },
  activeChip: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
  inactiveChip: {
    backgroundColor: '#f5f5f5',
    color: '#757575',
  },
  lastSynced: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  emptySync: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  statRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
  },
  statIcon: {
    marginRight: theme.spacing(1.5),
    color: theme.palette.text.secondary,
  },
  statCount: {
    fontWeight: 700,
    fontSize: '1.5rem',
    marginRight: theme.spacing(1),
  },
}));

interface DashboardContentProps {
  api: DevxpApi;
}

export const DashboardContent = ({ api }: DashboardContentProps) => {
  const classes = useStyles();
  const [config, setConfig] = useState<DevxpConfig | null>(null);
  const [loading, setLoading] = useState(true);

  // Developer unmask
  const [devMaskedInput, setDevMaskedInput] = useState('');
  const [devUnmaskResult, setDevUnmaskResult] = useState<UnmaskResult | null>(null);
  const [devUnmaskError, setDevUnmaskError] = useState('');

  // Repository unmask
  const [repoMaskedInput, setRepoMaskedInput] = useState('');
  const [repoUnmaskResult, setRepoUnmaskResult] = useState<UnmaskResult | null>(null);
  const [repoUnmaskError, setRepoUnmaskError] = useState('');

  const [syncConfigs, setSyncConfigs] = useState<GithubSyncConfig[]>([]);
  const [syncConfigsLoading, setSyncConfigsLoading] = useState(true);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const cfg = await api.getConfig();
      setConfig(cfg);
    } catch (e) {
      // Config not available
    } finally {
      setLoading(false);
    }
  }, [api]);

  const loadSyncConfigs = useCallback(async () => {
    try {
      setSyncConfigsLoading(true);
      const result = await api.getGithubSyncConfigs();
      setSyncConfigs(result.configs);
    } catch {
      // failed silently
    } finally {
      setSyncConfigsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadConfig();
    loadSyncConfigs();
    api.triggerAutoSync().catch(() => {/* silent */});
  }, [loadConfig, loadSyncConfigs, api]);

  const handleDevUnmask = async () => {
    if (!devMaskedInput.trim()) return;
    setDevUnmaskError('');
    setDevUnmaskResult(null);
    try {
      const result = await api.unmask(devMaskedInput.trim());
      setDevUnmaskResult(result);
    } catch (e: any) {
      setDevUnmaskError(e.message || 'Failed to unmask');
    }
  };

  const handleRepoUnmask = async () => {
    if (!repoMaskedInput.trim()) return;
    setRepoUnmaskError('');
    setRepoUnmaskResult(null);
    try {
      const result = await api.unmaskRepo(repoMaskedInput.trim());
      setRepoUnmaskResult(result);
    } catch (e: any) {
      setRepoUnmaskError(e.message || 'Failed to unmask');
    }
  };

  if (loading) {
    return <Typography>Loading configuration...</Typography>;
  }

  return (
    <Grid container spacing={3}>
      {/* Configuration Status */}
      <Grid item xs={12} md={6}>
        <InfoCard title="Configuration Status">
          {config ? (
            <Box>
              <ConfigItem
                label="Mode"
                value={config.masked ? 'Masked' : 'Unmasked'}
                ok
              />
              <ConfigItem
                label="Salt"
                value={config.saltConfigured ? 'Configured' : 'Not configured'}
                ok={config.saltConfigured}
              />
              <ConfigItem
                label="API Endpoint"
                value={config.apiEndpointConfigured ? 'Configured' : 'Not configured'}
                ok={config.apiEndpointConfigured}
              />
              <ConfigItem
                label="API Token"
                value={config.apiTokenConfigured ? 'Configured' : 'Not configured'}
                ok={config.apiTokenConfigured}
              />
              <ConfigItem
                label="Project ID"
                value={config.projectIdConfigured ? 'Configured' : 'Not configured'}
                ok={config.projectIdConfigured}
              />
            </Box>
          ) : (
            <Typography color="error">
              Unable to load configuration. Is the backend plugin installed?
            </Typography>
          )}
        </InfoCard>
      </Grid>

      {/* Mapping Database Stats */}
      <Grid item xs={12} md={6}>
        <InfoCard title="Mapping Database">
          <Box className={classes.statRow}>
            <PersonIcon className={classes.statIcon} />
            <Typography className={classes.statCount}>
              {config?.mappingCount ?? 0}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              developer identity mappings
            </Typography>
          </Box>
          <Box className={classes.statRow}>
            <StorageIcon className={classes.statIcon} />
            <Typography className={classes.statCount}>
              {config?.repoMappingCount ?? 0}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              repository name mappings
            </Typography>
          </Box>
          <Box mt={1}>
            <Typography variant="body2" color="textSecondary">
              Developer mappings: upload CSV or run GitHub sync in Settings.
              Repository mappings: upload CSV or run GitHub sync in Settings.
            </Typography>
          </Box>
        </InfoCard>
      </Grid>

      {/* GitHub Auto-Sync Configurations */}
      <Grid item xs={12}>
        <InfoCard title="GitHub Auto-Sync Configurations">
          {syncConfigsLoading ? (
            <Typography variant="body2" color="textSecondary">
              Loading sync configurations...
            </Typography>
          ) : syncConfigs.length === 0 ? (
            <Typography className={classes.emptySync} variant="body2">
              No GitHub sync configurations. Go to Settings to register a GitHub App for automatic member and repository syncing.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Organization</TableCell>
                    <TableCell>GitHub Host</TableCell>
                    <TableCell>Client ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Synced</TableCell>
                    <TableCell>Registered</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {syncConfigs.map(cfg => (
                    <TableRow key={cfg.id}>
                      <TableCell>
                        <strong>{cfg.org_name}</strong>
                      </TableCell>
                      <TableCell style={{ fontSize: '0.85em' }}>
                        {cfg.github_hostname ?? 'github.com'}
                      </TableCell>
                      <TableCell style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                        {cfg.app_client_id}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={cfg.active ? 'Active' : 'Inactive'}
                          size="small"
                          className={cfg.active ? classes.activeChip : classes.inactiveChip}
                        />
                      </TableCell>
                      <TableCell>
                        {cfg.last_synced_at ? (
                          <Typography className={classes.lastSynced}>
                            {new Date(cfg.last_synced_at).toLocaleString()}
                          </Typography>
                        ) : (
                          <Typography className={classes.lastSynced}>Never</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography className={classes.lastSynced}>
                          {new Date(cfg.created_at).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </InfoCard>
      </Grid>

      {/* Unmask Developer Name */}
      <Grid item xs={12} md={6}>
        <InfoCard title="Unmask Developer Name">
          <Typography variant="body2" color="textSecondary" paragraph>
            Enter a masked developer username (16-character hex hash) to look up
            the original name from the mapping database.
          </Typography>
          <Box display="flex" alignItems="flex-start">
            <TextField
              label="Masked username"
              value={devMaskedInput}
              onChange={e => setDevMaskedInput(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && handleDevUnmask()}
              variant="outlined"
              size="small"
              placeholder="e.g. a1b2c3d4e5f67890"
              style={{ minWidth: 260 }}
              inputProps={{ maxLength: 16 }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleDevUnmask}
              disabled={!devMaskedInput.trim()}
              style={{ marginLeft: 12, height: 40 }}
            >
              Unmask
            </Button>
          </Box>
          {devUnmaskResult && (
            <Box className={classes.unmaskResult}>
              <Typography className={classes.resultLabel} variant="body2">Masked:</Typography>
              <Typography className={classes.resultValue}>{devUnmaskResult.maskedName}</Typography>
              <Box mt={1}>
                <Typography className={classes.resultLabel} variant="body2">Real Name:</Typography>
                <Typography className={classes.resultValue}>
                  {devUnmaskResult.realName ? (
                    <Chip label={devUnmaskResult.realName} color="primary" variant="outlined" />
                  ) : (
                    <Chip label="No mapping found" color="default" variant="outlined" />
                  )}
                </Typography>
              </Box>
            </Box>
          )}
          {devUnmaskError && (
            <Box mt={2}>
              <Typography color="error">{devUnmaskError}</Typography>
            </Box>
          )}
        </InfoCard>
      </Grid>

      {/* Unmask Repository Name */}
      <Grid item xs={12} md={6}>
        <InfoCard title="Unmask Repository Name">
          <Typography variant="body2" color="textSecondary" paragraph>
            Enter a masked repository name (16-character hex hash) to look up
            the original repository name from the mapping database.
          </Typography>
          <Box display="flex" alignItems="flex-start">
            <TextField
              label="Masked repo name"
              value={repoMaskedInput}
              onChange={e => setRepoMaskedInput(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && handleRepoUnmask()}
              variant="outlined"
              size="small"
              placeholder="e.g. a1b2c3d4e5f67890"
              style={{ minWidth: 260 }}
              inputProps={{ maxLength: 16 }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleRepoUnmask}
              disabled={!repoMaskedInput.trim()}
              style={{ marginLeft: 12, height: 40 }}
            >
              Unmask
            </Button>
          </Box>
          {repoUnmaskResult && (
            <Box className={classes.unmaskResult}>
              <Typography className={classes.resultLabel} variant="body2">Masked:</Typography>
              <Typography className={classes.resultValue}>{repoUnmaskResult.maskedName}</Typography>
              <Box mt={1}>
                <Typography className={classes.resultLabel} variant="body2">Repository:</Typography>
                <Typography className={classes.resultValue}>
                  {repoUnmaskResult.realName ? (
                    <Chip label={repoUnmaskResult.realName} color="primary" variant="outlined" />
                  ) : (
                    <Chip label="No mapping found" color="default" variant="outlined" />
                  )}
                </Typography>
              </Box>
            </Box>
          )}
          {repoUnmaskError && (
            <Box mt={2}>
              <Typography color="error">{repoUnmaskError}</Typography>
            </Box>
          )}
        </InfoCard>
      </Grid>
    </Grid>
  );
};

function ConfigItem({
  label,
  value,
  ok,
}: {
  label: string;
  value: string;
  ok: boolean;
}) {
  return (
    <Box display="flex" alignItems="center" mb={1}>
      {ok ? (
        <CheckCircleIcon style={{ color: '#4caf50', marginRight: 8 }} fontSize="small" />
      ) : (
        <ErrorIcon style={{ color: '#f44336', marginRight: 8 }} fontSize="small" />
      )}
      <Typography variant="body2">
        <strong>{label}:</strong> {value}
      </Typography>
    </Box>
  );
}
